import styled, { css, keyframes } from 'styled-components';

import { Text } from '@/nova/components';
import { hoverWash } from '@/nova/theme/mixins';
import { CUST, CUST_ANIM, CUST_GRADIENTS, CUST_REDUCED_MOTION } from '../../../../theme/customizer-tokens';

/** Absolutely pinned to the bottom of the mobile screen so the button sits at the
 *  EXACT same position on every screen (intro + steps), never shifting. The scrim
 *  fades scrolling content into the page bg behind it. */
export const Bar = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 5;
  padding: 24px 10px calc(24px + env(safe-area-inset-bottom));
  background: ${CUST_GRADIENTS.mobileFooterScrim};
  pointer-events: none;
`;

export const Cta = styled.button<{ $enabled: boolean; $green?: boolean; $locked?: boolean }>`
  pointer-events: ${({ $locked }) => ($locked ? 'none' : 'auto')};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 48px;
  border: none;
  border-radius: 9999px;
  cursor: ${({ $enabled }) => ($enabled ? 'pointer' : 'not-allowed')};
  /* Cursor-following hover glow + lit hairline ring (shared with the CTA Button). */
  ${hoverWash}
  ${({ $enabled }) =>
    !$enabled &&
    css`
      &:hover::before,
      &:hover::after,
      &:focus-visible::before,
      &:focus-visible::after {
        opacity: 0;
      }
    `}
  background-color: ${({ $enabled, $green }) =>
    !$enabled ? CUST.ctaDisabledBg : $green ? CUST.deployGreen : CUST.accent};
  color: ${({ $green }) => ($green ? CUST.deployGreenText : CUST.ctaText)};
  /* Enabled⇄disabled (and orange⇄green) fade smoothly between steps instead of
     hard-swapping; hover stays snappy. */
  transition:
    background-color ${CUST_ANIM.CTA_FADE_MS}ms ${CUST_ANIM.SCREEN_EASE},
    color ${CUST_ANIM.CTA_FADE_MS}ms ${CUST_ANIM.SCREEN_EASE},
    filter ${CUST_ANIM.INTERACT_MS}ms ease;

  &:hover {
    filter: ${({ $enabled }) => ($enabled ? 'brightness(1.06)' : 'none')};
  }

  ${CUST_REDUCED_MOTION} {
    transition: filter ${CUST_ANIM.INTERACT_MS}ms ease;
  }
`;

/** New label fades + lifts in when the CTA copy changes between steps (keyed on
 *  the text in the component), so the wording cross-fades rather than snapping. */
const labelIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const Label = styled(Text)`
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  white-space: nowrap;
  animation: ${labelIn} ${CUST_ANIM.CTA_FADE_MS}ms ${CUST_ANIM.SCREEN_EASE};

  ${CUST_REDUCED_MOTION} {
    animation: none;
  }
`;